import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useSort, SortableHeader } from '../../components/SortableHeader';
import {
  ColFilterButton, cascadeRows, passesColumnFilter, type ColumnFilterValue,
} from '../../components/ColumnFilterDropdown';
import type { HeaderMenuSort } from '../../components/AppDataGrid';

/** Why the position-based actions are locked while the view is sorted/filtered. */
export const SEQ_VIEW_LOCK_MSG =
  'Clear the grid sort / filter first — the run order can only be changed in run-order view.';

/** Frozen column key → the qcBatch.css width variable its sticky `left` chain uses. */
const FROZEN_WIDTH_VAR: Record<string, string> = {
  qc: '--w-qct', sort: '--w-sort', code: '--w-sys', job: '--w-job', component: '--w-cmp', sid: '--w-sid',
};

/**
 * Inline style for `.seqwrap` that collapses a HIDDEN frozen column's width variable
 * to 0, so every later frozen column's sticky `left` offset re-flows (the same
 * technique .presort uses for the drag/remove columns). Inline wins over the CSS.
 */
export function frozenWidthVars(isHidden: (key: string) => boolean): CSSProperties {
  const style: Record<string, string> = {};
  for (const [key, cssVar] of Object.entries(FROZEN_WIDTH_VAR)) if (isHidden(key)) style[cssVar] = '0px';
  return style as CSSProperties;
}

export interface SeqGridViewOptions<T> {
  /** Displayed text of a cell — the SAME text the <td> shows (filter values + default sort). */
  text: (row: T, key: string) => string;
  /** Raw value to sort by (dates / numbers); defaults to `text`. */
  sortValue?: (row: T, key: string) => unknown;
  /** Keys of the columns currently shown. A sort/filter on a hidden column is ignored. */
  keys: readonly string[];
  /** The view resets (sort + filters cleared) whenever this value changes. */
  resetOn?: unknown;
}

export interface SeqGridView<T> {
  /** Rows to RENDER — filtered, then sorted. Never feed these to save / export / renumber. */
  viewRows: T[];
  /** True while a sort or filter is applied — position-based actions must be locked. */
  active: boolean;
  /** Back to the plain run order (clears sort + every filter). */
  clear: () => void;
  /** Header click → advance the sort (asc → desc → none), as on every other grid. */
  onSort: (key: string) => void;
  /** Header cell content: label + shared sort indicator + shared Excel-style filter chevron. */
  header: (key: string, label: string) => ReactNode;
  /** Pass to `useHeaderMenu` so the right-click Sort items work. */
  menuSort: HeaderMenuSort;
}

/**
 * VIEW-ONLY sort + filter for the QC Batch / Prep Batch run-sequence grids.
 *
 * Those grids are hand-rolled tables (frozen columns, drag-reorder, pre/post-Sort
 * editors), so they cannot use <AppDataGrid>; this hook gives them the SAME shared
 * pieces every other grid uses (`useSort` + `<SortableHeader>` + `<ColFilterButton>`,
 * CLAUDE.md rule 8). It only changes what is DISPLAYED: the caller's `rows` array —
 * the run order that is saved, exported and renumbered — is never reordered, and the
 * Sort numbers are never touched. While `active`, the caller locks every action that
 * depends on a row's POSITION (drag, Remove, Add QC, Add Dilution, pre-Sort select-all).
 */
export function useSeqGridView<T>(
  rows: readonly T[],
  { text, sortValue, keys, resetOn }: SeqGridViewOptions<T>,
): SeqGridView<T> {
  const sorter = useSort<T>();
  const { setSortExplicit } = sorter;
  const [filters, setFilters] = useState<Record<string, ColumnFilterValue>>({});

  // A rebuilt sequence (Sort / Previous / reload) starts back in run order.
  useEffect(() => {
    setSortExplicit(null, null);
    setFilters({});
  }, [resetOn, setSortExplicit]);

  const keySig = keys.join('|');
  const keySet = useMemo(() => new Set(keySig.split('|')), [keySig]);
  const liveFilters = useMemo(() => {
    const out: Record<string, ColumnFilterValue> = {};
    for (const [k, v] of Object.entries(filters)) if (v !== null && keySet.has(k)) out[k] = v;
    return out;
  }, [filters, keySet]);
  const sortOn = sorter.sort.key !== null && sorter.sort.dir !== null && keySet.has(sorter.sort.key);
  const active = sortOn || Object.keys(liveFilters).length > 0;

  const valueOf = sortValue ?? text;
  const filterEntries = Object.entries(liveFilters);
  const filtered = filterEntries.length === 0
    ? rows as T[]
    : rows.filter(r => filterEntries.every(([k, sel]) => passesColumnFilter(sel, text(r, k))));
  const viewRows = sortOn ? sorter.apply(filtered, valueOf) : filtered;

  const header = (key: string, label: string): ReactNode => (
    <SortableHeader label={label} sortKey={key} sort={sorter.sort}>
      {/* The filter popup is portaled to <body>, but React events still bubble
          through the component tree — without this wrapper every tick inside the
          popup would also reach the <th onClick> and flip the column sort. */}
      <span style={{ display: 'contents' }} onClick={e => e.stopPropagation()}
            onContextMenu={e => e.stopPropagation()}>
      <ColFilterButton<T>
        variant="inline"
        col={key}
        label={label}
        rows={cascadeRows(rows, liveFilters, key, text)}
        formatter={r => text(r, key)}
        sortDir={sorter.sort.key === key && sorter.sort.dir ? sorter.sort.dir : 'asc'}
        sortKey={r => valueOf(r, key)}
        value={filters[key] ?? null}
        onChange={v => setFilters(prev => ({ ...prev, [key]: v }))}
      />
      </span>
    </SortableHeader>
  );

  return {
    viewRows,
    active,
    clear: () => { setSortExplicit(null, null); setFilters({}); },
    onSort: sorter.setSort,
    header,
    menuSort: sorter,
  };
}
